import { CheckIcon } from "@/Icons/v2/Check";
import { ChevronIcon } from "@/Icons/v2/Chevron";
import { FunnelIcon } from "@/Icons/v2/Funnel";
import { useTerminalsState } from "@/States/Terminals";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { trpc } from "@/Utils/trpc";
import { useTrans } from "@/translations";

import { useCallback, useEffect, useMemo, useRef } from "react";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { uniq } from "lodash";

import { type TimeFilter, defaultTimeFilter } from "../DatePicker/constants";
import { ViewWithLoader } from "../ViewWithLoader";

type Props = {
  timeFilter?: TimeFilter;
  value: string[] | null;
  onChange: (categories: string[] | null) => void;
  setCurrentViewLabel?: (value: string) => void;
};

export const TenantCategoriesSelector: React.FC<Props> = ({
  timeFilter,
  value,
  onChange,
  setCurrentViewLabel,
}) => {
  const { t } = useTrans();
  const { tenantCategories } = useCommonEntitiesStore();
  const { currentTerminal } = useTerminalsState();

  const { data: customersData, isLoading: isLoadingCategoriesData } =
    trpc.dwh.customers.get.useQuery(
      {
        ...(timeFilter || defaultTimeFilter),
        terminalId: currentTerminal?.id || "",
      },
      {
        queryKeyHashFn: () =>
          [
            "customers",
            currentTerminal?.id,
            timeFilter?.startTimeISO,
            timeFilter?.endTimeISO,
          ].toString(),
        enabled: !!timeFilter && !!currentTerminal,
      },
    );

  const displayCategories = useMemo(() => {
    if (!customersData || !tenantCategories) return [];

    const categoriesFromData = uniq(
      customersData.customers.map((c) => c.commercialCustomerCategory),
    );

    return tenantCategories.filter((c) => categoriesFromData.includes(c.id));
  }, [customersData, tenantCategories]);

  // Categories differ per building, so reset the selection when it changes;
  // the effect below then selects all of the new building's categories.
  const previousTerminalId = useRef(currentTerminal?.id);
  useEffect(() => {
    if (previousTerminalId.current === currentTerminal?.id) return;
    previousTerminalId.current = currentTerminal?.id;
    onChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTerminal?.id]);

  useEffect(() => {
    if (!displayCategories.length || value) return;
    onChange(displayCategories.map((c) => c.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, displayCategories]);

  const selected = useMemo(() => value ?? [], [value]);
  const isAllSelected = useMemo(
    () => selected.length === 0 || selected.length === displayCategories.length,
    [displayCategories.length, selected.length],
  );

  const label = useMemo(() => {
    if (isAllSelected) return t("homePage:categorySelectorAll");
    if (selected.length === 1)
      return displayCategories.find((c) => c.id === selected[0])?.name || "";
    return t("homePage:categorySelectorNrSelected", {
      nrSelected: selected.length,
    });
  }, [isAllSelected, selected, displayCategories, t]);

  useEffect(() => {
    if (setCurrentViewLabel) {
      setCurrentViewLabel(label);
    }
  }, [label, setCurrentViewLabel]);

  const toggleCategory = useCallback(
    (category: string) => {
      const next = selected.includes(category)
        ? selected.filter((c) => c !== category)
        : [...selected, category];
      onChange(next);
    },
    [onChange, selected],
  );

  const selectAll = useCallback(
    () => onChange(displayCategories.map((c) => c.id).slice()),
    [displayCategories, onChange],
  );
  const clearAll = useCallback(() => onChange([]), [onChange]);

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <PopoverButton
            className={`flex h-[40px] min-w-[150px] cursor-pointer items-center gap-[9px] rounded-md border px-[13px] py-[9px] text-[13px] font-medium max-[1200px]:h-[34px] max-[1200px]:min-w-[34px] max-[1200px]:px-[9px] ${
              !isAllSelected && !isLoadingCategoriesData
                ? "bg-lime-light border-black text-black"
                : "border-grey3 bg-white text-black"
            }`}
          >
            <ViewWithLoader isLoading={isLoadingCategoriesData}>
              <FunnelIcon />
              <span className="max-[1200px]:hidden">{label}</span>
              <ChevronIcon open={open} className="max-[1200px]:hidden" />
            </ViewWithLoader>
          </PopoverButton>

          <PopoverPanel
            anchor={{ to: "bottom start", gap: 4.5 }}
            className="border-grey3 z-[10000] w-[250px] overflow-hidden rounded-[8.25px] border bg-white shadow-[0_3px_9px_rgba(0,0,0,0.08)]"
          >
            <div className="px-[14px] pt-[12px] pb-[8px]">
              <div className="text-grey6 mb-1.5 text-[10.5px] font-bold tracking-wider uppercase">
                {t("homePage:categorySelectorTitle")}
              </div>
              <div className="flex gap-[8px]">
                <button
                  type="button"
                  onClick={selectAll}
                  className="border-grey3 hover:bg-grey1 flex-1 cursor-pointer rounded-[4.95px] border bg-white py-[5px] text-[11.5px] font-semibold text-black"
                >
                  {t("homePage:categorySelectorSelectAll")}
                </button>
                <button
                  type="button"
                  onClick={clearAll}
                  className="border-grey3 hover:bg-grey1 flex-1 cursor-pointer rounded-[4.95px] border bg-white py-[5px] text-[11.5px] font-semibold text-black"
                >
                  {t("homePage:categorySelectorClear")}
                </button>
              </div>
            </div>

            <div className="px-2 pb-2">
              {displayCategories.map((category) => {
                const checked = selected.includes(category.id);
                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => toggleCategory(category.id)}
                    className="hover:bg-grey1 flex w-full cursor-pointer items-center gap-[10px] rounded-[4.95px] px-2 py-2 text-left text-[13px] text-black"
                  >
                    <span
                      className={`flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-[3.3px] border ${
                        checked
                          ? "border-black bg-black"
                          : "border-grey4 bg-white"
                      }`}
                    >
                      {checked && <CheckIcon />}
                    </span>
                    {category.name}
                  </button>
                );
              })}
            </div>
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};
